package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.pages.app.projects.slug.storeVaultUnconfiguredMessage
import kotlinx.browser.window
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.longOrNull
import org.w3c.dom.events.Event
import dev.lingvo.site.i18n.t

/** Makes the role boundary readable without a page full of disabled controls. */
fun screenshotsReadOnlyMessage(): String = tr("app.projects.slug.screenshots.screenshots_states.viewers_can_see_the_plan_the",
    "Viewers can see the plan, the designs and what went live. Translators upload captures and edit captions; owners and admins change the plan and push.")

/** Byte budgets come from the error details, never from a stale plan or marketing tier limit. */
fun screenshotsQuotaExceededMessage(details: JsonObject): String {
    fun number(key: String): Long? = runCatching { details[key]?.jsonPrimitive?.longOrNull }.getOrNull()?.takeIf { it >= 0 }
    val scope = runCatching { details["scope"]?.jsonPrimitive?.content }.getOrNull()
    val used = number("usedBytes")
    val limit = number("limitBytes")
    val needed = number("neededBytes")
    if (used == null || limit == null || needed == null || scope !in setOf("project", "workspace")) return tr("app.projects.slug.screenshots.screenshots_states.screenshot_storage_is_full_delete_images",
        "Screenshot storage is full. Delete images you no longer need, or choose a plan with more storage.")
    return if (scope == "project") trf("screenshots.state.quota_project",
        "This project uses {used} of {limit} bytes of screenshot storage. This upload needs {needed} more bytes. Delete images you no longer need, or choose a plan with more storage.",
        "used" to used.toString(), "limit" to limit.toString(), "needed" to needed.toString())
    else trf("screenshots.state.quota_workspace",
        "This workspace uses {used} of {limit} bytes of screenshot storage. This upload needs {needed} more bytes. Delete images you no longer need, or choose a plan with more storage.",
        "used" to used.toString(), "limit" to limit.toString(), "needed" to needed.toString())
}

/** Preserves a store's specific version-state explanation before using general advice. */
fun screenshotsVersionLockedMessage(serverMessage: String? = null): String = serverMessage?.takeIf { it.isNotBlank() }
    ?: tr("app.projects.slug.screenshots.screenshots_states.the_app_store_has_no_editable", "The App Store has no editable version right now. Create one in App Store Connect, then push again.")

/** Explains why a design temporarily opens read-only during delivery. */
fun screenshotsPushRunningMessage(): String = tr("app.projects.slug.screenshots.screenshots_states.a_push_of_this_set_is",
    "A push of this set is running. Editing opens again when it finishes.")

/** Keeps screenshot review consistent with the existing store connection notice. */
fun screenshotsVaultOffMessage(): String = storeVaultUnconfiguredMessage()

/** Offline state is local to the tab and must not suggest a successful server save. */
fun screenshotsOfflineMessage(): String = tr("app.projects.slug.screenshots.screenshots_states.youre_offline_changes_stay_in_this",
    "You're offline. Changes stay in this tab and are saved when you reconnect.")

/** Renders the role explanation once at the page boundary. */
@Composable
fun ScreenshotsReadOnlyState() { LAlert(title = screenshotsReadOnlyMessage()) }

/** Uses authoritative quota details to explain blocked upload and compose controls. */
@Composable
fun ScreenshotsQuotaState(details: JsonObject) { LAlert(title = screenshotsQuotaExceededMessage(details), variant = LAlertVariant.WARNING) }

/** Presents a blocked store version as actionable amber state. */
@Composable
fun ScreenshotsVersionLockedState(serverMessage: String? = null) {
    LAlert(title = screenshotsVersionLockedMessage(serverMessage), variant = LAlertVariant.WARNING)
}

/** Keeps the temporary push lock visible to readers. */
@Composable
fun ScreenshotsPushRunningState() { LAlert(title = screenshotsPushRunningMessage()) }

/** Reuses the stores vault setup explanation. */
@Composable
fun ScreenshotsVaultOffState() { LAlert(title = screenshotsVaultOffMessage(), variant = LAlertVariant.WARNING) }

/** Installs exactly one online/offline listener pair and removes both with the composition. */
@Composable
fun ScreenshotsOfflineState() {
    var offline by remember { mutableStateOf(!window.navigator.onLine) }
    DisposableEffect(Unit) {
        val update: (Event) -> Unit = { offline = !window.navigator.onLine }
        window.addEventListener("online", update)
        window.addEventListener("offline", update)
        onDispose {
            window.removeEventListener("online", update)
            window.removeEventListener("offline", update)
        }
    }
    if (offline) LAlert(title = screenshotsOfflineMessage(), live = true)
}
